import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Phone, CheckCircle, Clock, Shield, MapPin, Euro, ArrowRight } from 'lucide-react';
import Breadcrumbs from '../components/Breadcrumbs';

const TueroeffnungZugefallenPage = () => {
  const phone = '+4923139085116';
  const displayPhone = '0231 39085116';

  const faqData = [
    {
      question: 'Wie schnell sind Sie bei mir?',
      answer: 'In der Regel erreichen wir Sie innerhalb von 15-30 Minuten in Dortmund. Die genaue Zeit hängt von Ihrem Standort und der aktuellen Verkehrslage ab.'
    },
    {
      question: 'Muss das Schloss bei einer zugefallenen Tür getauscht werden?',
      answer: 'Nein, in den meisten Fällen nicht. Bei zugefallenen Türen ist die Chance auf eine schadenfreie Öffnung sehr hoch. Das Schloss bleibt intakt und muss nicht ausgetauscht werden.'
    },
    {
      question: 'Was kostet die Türöffnung genau?',
      answer: 'Unsere Festpreise: Mo–Fr 08–20 Uhr: 119 €, Sa–So: 199 €. Die Anfahrt ist bereits im Preis enthalten. Es gibt keine versteckten Kosten.'
    },
    {
      question: 'Wie hoch ist die Chance auf eine schadenfreie Öffnung?',
      answer: 'Bei zugefallenen Türen liegt die Chance auf eine schadenfreie Öffnung bei über 95%. Wir verwenden professionelles Werkzeug und schonende Öffnungstechniken.'
    },
    {
      question: 'Kann ich auch mit Karte zahlen?',
      answer: 'Ja, wir akzeptieren EC-Karten und alle gängigen Kreditkarten (Visa, Mastercard). Barzahlung ist selbstverständlich auch möglich.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Türöffnung zugefallene Tür in Dortmund – Schlüsseldienst zum Festpreis</title>
        <meta name="description" content="Ihre Tür ist zugefallen? Schlüsseldienst Dortmund öffnet zugefallene Türen schnell & zum Festpreis. Anfahrt inklusive. Jetzt anrufen: 0231 39085116." />
        <link rel="canonical" href="https://schluesseldienst.services/leistungen/tueroeffnung-zugefallen/" />
      </Helmet>

      <div className="container mx-auto px-4 py-12">
        <Breadcrumbs items={[
          { label: 'Leistungen', path: '/leistungen' },
          { label: 'Türöffnung zugefallen' }
        ]} />
        
        <h1 className="text-4xl sm:text-5xl font-bold mb-8 text-gray-900">
          Türöffnung bei zugefallener Tür in Dortmund
        </h1>

        {/* Einleitung */}
        <div className="max-w-4xl mb-12">
          <p className="text-lg text-gray-700 mb-4">
            <strong>Keine Panik!</strong> Es passiert schneller als gedacht: Ein Windzug, die Tür fällt ins Schloss – 
            und der Schlüssel liegt noch in der Wohnung. Diese Situation kennen viele Menschen und ist einer 
            der häufigsten Gründe für einen Schlüsseldienst-Einsatz.
          </p>
          <p className="text-lg text-gray-700 mb-4">
            Als erfahrener Schlüsseldienst in Dortmund helfen wir Ihnen schnell und professionell weiter. 
            In den meisten Fällen können wir zugefallene Türen ohne jede Beschädigung öffnen – Sie müssen 
            also kein neues Schloss kaufen.
          </p>
          <p className="text-lg text-gray-700 mb-6">
            Die Schössow Dienstleistungen UG ist 24 Stunden am Tag für Sie erreichbar. Wir kommen schnell 
            zu Ihnen nach Hause, öffnen die Tür fachgerecht und rechnen transparent nach Festpreisen ab.
          </p>
          
          {/* Schnell-CTA */}
          <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-6 mb-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Sofort Hilfe benötigt?</h3>
                <p className="text-gray-700">Rufen Sie uns an – wir sind in 15-30 Minuten bei Ihnen!</p>
              </div>
              <a
                href={`tel:${phone}`}
                className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-full font-bold text-lg transition-all shadow-lg whitespace-nowrap"
              >
                <Phone size={20} />
                {displayPhone}
              </a>
            </div>
          </div>
        </div>

        {/* Vorteile */}
        <div className="max-w-4xl mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">Vorteile auf einen Blick</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex gap-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <p className="text-gray-700"><strong>Schnelle Anfahrt</strong> in ganz Dortmund – meist innerhalb von 15-30 Minuten</p>
            </div>
            <div className="flex gap-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <p className="text-gray-700"><strong>Hohe Chance auf schadenfreie Öffnung</strong> – über 95% ohne Beschädigung</p>
            </div>
            <div className="flex gap-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <p className="text-gray-700"><strong>Fester Preis</strong> je nach Tageszeit, Anfahrt bereits inklusive</p>
            </div>
            <div className="flex gap-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <p className="text-gray-700"><strong>EC- und Kartenzahlung</strong> möglich – kein Bargeld nötig</p>
            </div>
            <div className="flex gap-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <p className="text-gray-700"><strong>24/7 Notdienst</strong> – auch nachts, am Wochenende und an Feiertagen</p>
            </div>
            <div className="flex gap-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <p className="text-gray-700"><strong>Professionelles Werkzeug</strong> – schonende Öffnungsmethoden</p>
            </div>
          </div>
        </div>

        {/* So läuft es ab */}
        <div className="max-w-4xl mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">So läuft die Türöffnung ab</h2>
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-600">
              <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
                <span className="bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">1</span>
                Anruf
              </h3>
              <p className="text-gray-700">
                Sie rufen uns unter <strong>{displayPhone}</strong> an und schildern kurz Ihre Situation. 
                Wir klären bereits am Telefon den voraussichtlichen Preis und die Anfahrtszeit.
              </p>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-600">
              <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
                <span className="bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">2</span>
                Schnelle Anfahrt
              </h3>
              <p className="text-gray-700">
                Unser Techniker macht sich sofort auf den Weg zu Ihnen. In der Regel sind wir innerhalb 
                von 15-30 Minuten vor Ort – je nach Stadtteil und Verkehrslage.
              </p>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-600">
              <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
                <span className="bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">3</span>
                Prüfung von Schloss & Tür
              </h3>
              <p className="text-gray-700">
                Vor Ort begutachten wir Tür und Schloss, um die beste Öffnungsmethode zu wählen. 
                Bei zugefallenen Türen ist meist eine schadenfreie Öffnung möglich.
              </p>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-600">
              <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
                <span className="bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">4</span>
                Öffnung ohne Beschädigungen
              </h3>
              <p className="text-gray-700">
                Mit professionellem Werkzeug und jahrelanger Erfahrung öffnen wir Ihre Tür. 
                In über 95% der Fälle gelingt dies ohne jede Beschädigung an Tür oder Schloss.
              </p>
            </div>
          </div>
        </div>

        {/* Preise */}
        <div className="max-w-4xl mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">Preise & Kosten</h2>
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-8 border-2 border-orange-200">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-white rounded-lg p-6 shadow-md">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-6 h-6 text-orange-600" />
                  <h3 className="text-xl font-bold">Mo–Fr 08–20 Uhr</h3>
                </div>
                <div className="text-4xl font-bold text-orange-600 mb-2">119 €</div>
                <p className="text-gray-700">Anfahrt inklusive</p>
              </div>
              
              <div className="bg-white rounded-lg p-6 shadow-md">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-6 h-6 text-orange-600" />
                  <h3 className="text-xl font-bold">Sa–So & Feiertage</h3>
                </div>
                <div className="text-4xl font-bold text-orange-600 mb-2">199 €</div>
                <p className="text-gray-700">Anfahrt inklusive</p>
              </div>
            </div>
            
            <div className="bg-white rounded-lg p-6">
              <h4 className="font-bold text-lg mb-3 flex items-center gap-2">
                <Shield className="w-5 h-5 text-green-600" />
                Transparenz & Fairness
              </h4>
              <ul className="space-y-2 text-gray-700">
                <li className="flex gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Der Preis wird bereits am Telefon genannt</span>
                </li>
                <li className="flex gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Keine versteckten Kosten oder Zusatzgebühren</span>
                </li>
                <li className="flex gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Anfahrt ist bereits im Festpreis enthalten</span>
                </li>
                <li className="flex gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Zahlung bar, per EC-Karte oder Kreditkarte</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Einsatzgebiet */}
        <div className="max-w-4xl mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">Einsatzgebiet in Dortmund</h2>
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex gap-3 mb-4">
              <MapPin className="w-6 h-6 text-orange-600 flex-shrink-0" />
              <p className="text-gray-700">
                <strong>Wir sind in ganz Dortmund für Sie im Einsatz.</strong> Egal ob Innenstadt, Hörde, 
                Hombruch, Aplerbeck, Eving, Scharnhorst, Mengede, Lütgendortmund oder ein anderer Stadtteil – 
                wir erreichen Sie schnell.
              </p>
            </div>
            <p className="text-gray-700 mb-4">
              Unsere Zentrale befindet sich zentral in Dortmund (Neben dem Brand 10, 44135 Dortmund), 
              sodass wir kurze Anfahrtswege in alle Stadtteile haben.
            </p>
            <div className="flex flex-wrap gap-2">
              {['Innenstadt', 'Hörde', 'Hombruch', 'Aplerbeck', 'Eving', 'Scharnhorst', 'Mengede', 
                'Lütgendortmund', 'Brackel', 'Huckarde', 'Dorstfeld'].map(stadtteil => (
                <span key={stadtteil} className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm font-medium">
                  {stadtteil}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="max-w-4xl mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">Häufige Fragen</h2>
          <div className="space-y-4">
            {faqData.map((faq, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold mb-3 text-gray-900">{faq.question}</h3>
                <p className="text-gray-700">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="bg-gray-900 text-white rounded-2xl p-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Tür zugefallen? Jetzt anrufen!</h2>
          <p className="text-lg mb-6 text-gray-300">
            Wir sind 24/7 für Sie erreichbar – auch nachts, am Wochenende und an Feiertagen.<br />
            Schnelle Anfahrt • Festpreise • Schadenfreie Öffnung
          </p>
          <a
            href={`tel:${phone}`}
            className="inline-flex items-center gap-3 bg-orange-600 hover:bg-orange-700 text-white px-8 py-4 rounded-full font-bold text-xl transition-all shadow-lg hover:shadow-xl"
          >
            <Phone size={24} />
            {displayPhone}
          </a>
        </div>

        {/* Interne Links */}
        <div className="mt-12 max-w-4xl">
          <h3 className="text-xl font-bold mb-4 text-gray-900">Weitere Leistungen</h3>
          <div className="flex flex-wrap gap-4">
            <Link 
              to="/leistungen"
              className="inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-800 px-6 py-3 rounded-full font-semibold transition-all"
            >
              Alle Leistungen
              <ArrowRight size={18} />
            </Link>
            <Link 
              to="/leistungen/tueroeffnung-verschlossen"
              className="inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-800 px-6 py-3 rounded-full font-semibold transition-all"
            >
              Türöffnung verschlossen
              <ArrowRight size={18} />
            </Link>
            <Link 
              to="/leistungen/schlosswechsel"
              className="inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-800 px-6 py-3 rounded-full font-semibold transition-all"
            >
              Schlosswechsel
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default TueroeffnungZugefallenPage;
